import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import {
  mvMetadataGapApi,
  BrandArtistLookupEntry,
  MvBrandArtistStatus,
} from "../../../api/mvMetadataGapApi";
import { withExportDate } from "../../../utils/exportFileName";

const STATUS_OPTIONS: { label: string; value: MvBrandArtistStatus }[] = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "No artist (non-music)", value: "approved_no_artist" },
];

function snapshotEntry(entry: BrandArtistLookupEntry): BrandArtistLookupEntry {
  return {
    ...entry,
    artist: entry.artist ?? null,
    notes: entry.notes ?? "",
  };
}

function isRowDirty(
  row: BrandArtistLookupEntry,
  baseline: Map<string, BrandArtistLookupEntry>
): boolean {
  const base = baseline.get(row.id);
  if (!base) return true;
  return (
    (row.artist ?? "") !== (base.artist ?? "") ||
    row.status !== base.status ||
    (row.notes ?? "") !== (base.notes ?? "")
  );
}

function resolveSaveStatus(row: BrandArtistLookupEntry): MvBrandArtistStatus {
  if (row.status === "pending" && row.artist?.trim()) return "approved";
  return row.status;
}

const BrandArtistLookupTab = () => {
  const toast = useRef<Toast>(null);
  const baselineRef = useRef<Map<string, BrandArtistLookupEntry>>(new Map());
  const [entries, setEntries] = useState<BrandArtistLookupEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filterStatus, setFilterStatus] = useState<MvBrandArtistStatus | "">("");
  const [csvText, setCsvText] = useState("");
  const [showImport, setShowImport] = useState(false);

  const setBaseline = (data: BrandArtistLookupEntry[]) => {
    baselineRef.current = new Map(
      data.map((entry) => [entry.id, snapshotEntry(entry)])
    );
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { entries: data } = await mvMetadataGapApi.getBrandArtistLookup(
        filterStatus ? { status: filterStatus } : undefined
      );
      setEntries(data);
      setBaseline(data);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load brand artist lookup",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const dirtyRows = useMemo(
    () => entries.filter((row) => isRowDirty(row, baselineRef.current)),
    [entries]
  );
  const dirtyCount = dirtyRows.length;

  const handleSeed = () => {
    confirmDialog({
      message: "Seed brands from Episodes, Compilations, and DerivedAssets? Existing rows are kept unless you force re-seed.",
      header: "Seed brand lookup",
      icon: "pi pi-database",
      accept: async () => {
        setSeeding(true);
        try {
          const result = await mvMetadataGapApi.seedBrandArtistLookup(false);
          toast.current?.show({
            severity: "success",
            summary: "Seeded",
            detail: `${result.seeded} added, ${result.skipped} skipped`,
            life: 4000,
          });
          await loadEntries();
        } catch {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Seed failed",
            life: 4000,
          });
        } finally {
          setSeeding(false);
        }
      },
    });
  };

  const handleSaveChanges = async () => {
    if (!dirtyCount) {
      toast.current?.show({
        severity: "info",
        summary: "Nothing to save",
        detail: "No unsaved changes",
        life: 2500,
      });
      return;
    }

    setSaving(true);
    const failures: string[] = [];
    const saved = new Map<string, BrandArtistLookupEntry>();

    for (const row of dirtyRows) {
      try {
        const status = resolveSaveStatus(row);
        const entry = await mvMetadataGapApi.saveBrandArtistLookup({
          brandKey: row.brandKey,
          artist: row.artist,
          status,
          notes: row.notes,
        });
        saved.set(row.id, entry);
      } catch (err: unknown) {
        const message =
          err &&
          typeof err === "object" &&
          "response" in err &&
          err.response &&
          typeof err.response === "object" &&
          "data" in err.response &&
          err.response.data &&
          typeof err.response.data === "object" &&
          "message" in err.response.data
            ? String(err.response.data.message)
            : "Save failed";
        failures.push(`${row.brandKey}: ${message}`);
      }
    }

    if (saved.size) {
      setEntries((prev) =>
        prev.map((row) => (saved.has(row.id) ? saved.get(row.id)! : row))
      );
      saved.forEach((entry, id) => {
        baselineRef.current.set(id, snapshotEntry(entry));
      });
    }

    if (failures.length === 0) {
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: `${saved.size} brand(s) updated`,
        life: 3000,
      });
    } else if (saved.size > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Partial save",
        detail: `${saved.size} saved, ${failures.length} failed`,
        life: 5000,
      });
      console.error("Brand lookup save failures:", failures);
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: failures[0] ?? "Could not save changes",
        life: 5000,
      });
    }

    setSaving(false);
  };

  const handleExportCsv = () => {
    const header = "brandKey,artist,status,notes";
    const lines = entries.map(
      (e) =>
        `"${e.brandKey.replace(/"/g, '""')}","${(e.artist ?? "").replace(/"/g, '""')}",${e.status},"${(e.notes ?? "").replace(/"/g, '""')}"`
    );
    const blob = new Blob([[header, ...lines].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${withExportDate("Brand Artist Lookup")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCsv = async () => {
    const lines = csvText.trim().split("\n").slice(1);
    const rows = lines
      .map((line) => {
        const match = line.match(/^"([^"]*)","([^"]*)",([^,]+),"([^"]*)"$/);
        if (!match) return null;
        return {
          brandKey: match[1],
          artist: match[2] || null,
          status: match[3] as MvBrandArtistStatus,
          notes: match[4] || undefined,
        };
      })
      .filter(Boolean) as Array<{
      brandKey: string;
      artist: string | null;
      status: MvBrandArtistStatus;
      notes?: string;
    }>;

    try {
      const result = await mvMetadataGapApi.importBrandArtistLookup(rows);
      toast.current?.show({
        severity: "success",
        summary: "Imported",
        detail: `${result.imported} rows`,
        life: 4000,
      });
      setShowImport(false);
      setCsvText("");
      await loadEntries();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Import failed",
        life: 4000,
      });
    }
  };

  const rowClassName = (row: BrandArtistLookupEntry) =>
    isRowDirty(row, baselineRef.current) ? "mv-brand-lookup-dirty" : "";

  const artistEditor = (row: BrandArtistLookupEntry) => (
    <InputText
      value={row.artist ?? ""}
      onChange={(e) => {
        const value = e.target.value;
        setEntries((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, artist: value || null } : r
          )
        );
      }}
      style={{ width: "100%" }}
      placeholder="Artist name (blank = non-music)"
    />
  );

  const statusEditor = (row: BrandArtistLookupEntry) => (
    <Dropdown
      value={row.status}
      options={STATUS_OPTIONS}
      onChange={(e) => {
        setEntries((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, status: e.value } : r))
        );
      }}
      style={{ width: "100%" }}
    />
  );

  const notesEditor = (row: BrandArtistLookupEntry) => (
    <InputText
      value={row.notes ?? ""}
      onChange={(e) => {
        setEntries((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, notes: e.target.value } : r
          )
        );
      }}
      style={{ width: "100%" }}
    />
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <style>{`
        .mv-brand-lookup-dirty {
          background-color: #fff8e6 !important;
        }
      `}</style>
      <Card title="Brand → Artist Lookup">
        <p style={{ marginBottom: "1rem", color: "#666" }}>
          Assign an artist for each brand (from Episodes, Compilations, and DerivedAssets). Edit rows below, then use{" "}
          <strong>Save changes</strong> once — unsaved rows stay highlighted until saved.
          Entering an artist auto-approves on save.
        </p>
        <div className="filter-container">
          <div className="grid-container">
            <div className="filter-item">
              <span className="mv-gap-filter-label">Status</span>
              <Dropdown
                value={filterStatus}
                options={[{ label: "All statuses", value: "" }, ...STATUS_OPTIONS]}
                onChange={(e) => setFilterStatus(e.value)}
                placeholder="All statuses"
                style={{ width: "100%", maxWidth: "280px", borderRadius: "5px" }}
              />
            </div>
          </div>
          <div className="mv-metadata-toolbar">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              outlined
              severity="secondary"
              onClick={loadEntries}
            />
            <Button
              label="Seed from catalog"
              icon="pi pi-database"
              outlined
              severity="info"
              loading={seeding}
              onClick={handleSeed}
            />
            <Button
              label="Export CSV"
              icon="pi pi-download"
              outlined
              severity="secondary"
              onClick={handleExportCsv}
            />
            <Button
              label="Import CSV"
              icon="pi pi-upload"
              outlined
              severity="secondary"
              onClick={() => setShowImport(true)}
            />
            <span className="mv-metadata-toolbar-divider" aria-hidden="true" />
            <Button
              label={dirtyCount ? `Save changes (${dirtyCount})` : "Save changes"}
              icon="pi pi-save"
              severity="success"
              loading={saving}
              disabled={!dirtyCount || saving}
              onClick={handleSaveChanges}
            />
          </div>
        </div>
        {showImport && (
          <div
            className="filter-container"
            style={{
              marginBottom: "1rem",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--surface-border)",
              background: "var(--surface-ground)",
            }}
          >
            <InputText
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder='Paste CSV: brandKey,artist,status,notes'
              style={{ width: "100%", marginBottom: "0.5rem" }}
            />
            <Button label="Run import" onClick={handleImportCsv} className="p-button-success" />
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowImport(false)}
            />
          </div>
        )}
        {loading ? (
          <ProgressSpinner />
        ) : (
          <DataTable
            value={entries}
            rowClassName={rowClassName}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            stripedRows
            size="small"
            emptyMessage="No brands — seed from Episodes to start."
          >
            <Column field="brandKey" header="Brand" sortable />
            <Column header="Artist" body={artistEditor} style={{ minWidth: "200px" }} />
            <Column header="Status" body={statusEditor} style={{ minWidth: "180px" }} />
            <Column header="Notes" body={notesEditor} style={{ minWidth: "160px" }} />
          </DataTable>
        )}
      </Card>
    </>
  );
};

export default BrandArtistLookupTab;
